import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AiMarketingFeatureHeaderComponent } from './ai-marketing-feature-header.component';
import { AiMarketingService } from '@admin-core/services/ai-marketing.service';
import { ProductService } from '@admin-core/services/product.service';
import { AiCreativeListItem, AiPublishDestination } from '@admin-core/models/ai-marketing.model';
import { Product, ProductListItem } from '@admin-core/models/product.model';
import { LanguageService } from '@admin-core/i18n/language.service';
import { TranslatePipe } from '@admin-core/i18n/translate.pipe';
import { TranslationKey } from '@admin-core/i18n/translations/en';
import { ConfirmDialogService } from '@admin-shared/components/confirm-dialog/confirm-dialog.service';
import { HttpLoadingService } from '@admin-core/services/http-loading.service';

@Component({
  selector: 'app-ai-marketing-creatives',
  standalone: true,
  imports: [FormsModule, TranslatePipe, DatePipe, AiMarketingFeatureHeaderComponent],
  templateUrl: './ai-marketing-creatives.component.html',
  styleUrl: './ai-marketing-admin.scss'
})
export class AiMarketingCreativesComponent implements OnInit {
  private readonly api = inject(AiMarketingService);
  private readonly productsApi = inject(ProductService);
  private readonly i18n = inject(LanguageService);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly httpLoading = inject(HttpLoadingService);

  private readonly prefetchQueue: string[] = [];
  private prefetchInFlight = 0;
  private readonly maxPrefetchConcurrent = 2;

  readonly items = signal<AiCreativeListItem[]>([]);
  readonly loading = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly uploading = signal(false);
  readonly creatingFromProduct = signal(false);
  readonly actionId = signal<string | null>(null);

  readonly productSearch = signal('');
  readonly productResults = signal<ProductListItem[]>([]);
  readonly loadingProducts = signal(false);
  readonly selectedProduct = signal<Product | null>(null);
  readonly selectedImageId = signal<string | null>(null);
  readonly productPrompt = signal('');
  private productSearchTimer: ReturnType<typeof setTimeout> | null = null;

  readonly selectedFile = signal<File | null>(null);

  readonly publishTarget = signal<AiCreativeListItem | null>(null);
  readonly publishPlatform = signal<string | null>(null);
  readonly publishCaption = signal('');
  readonly publishOptionsByCreativeId = signal<Record<string, AiPublishDestination[]>>({});
  readonly loadingPublishOptionsIds = signal<Record<string, boolean>>({});
  readonly publishing = signal(false);

  readonly showCreateForm = signal(false);

  readonly status = signal('');
  readonly type = signal('');
  readonly source = signal('');

  readonly statuses = ['Generated', 'Approved'] as const;
  readonly types = ['Image', 'Video'] as const;
  readonly sources = ['UserUpload', 'ProductCatalog'] as const;

  readonly filtered = computed(() => {
    const status = this.status();
    const type = this.type();
    const source = this.source();
    return this.items().filter((c) => {
      if (status && c.status !== status) return false;
      if (type && c.type !== type) return false;
      if (source && c.source !== source) return false;
      return true;
    });
  });

  readonly activePublishDestination = computed(() => {
    const creative = this.publishTarget();
    const platform = this.publishPlatform();
    if (!creative || !platform) {
      return null;
    }
    return this.getDestinations(creative).find((d) => d.platform === platform) ?? null;
  });

  readonly canConfirmPublish = computed(() => this.activePublishDestination()?.canPublish === true);

  readonly publishingStatusLabel = computed(() => {
    if (!this.publishing()) {
      return '';
    }
    const platform = this.publishPlatform();
    if (!platform) {
      return this.i18n.t('aiMarketing.creatives.publishing');
    }
    return this.i18n.t('aiMarketing.creatives.publishingTo', {
      platform: this.i18n.t(this.platformKey(platform))
    });
  });

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.items.set([]);
    this.publishOptionsByCreativeId.set({});
    this.loadingPublishOptionsIds.set({});
    this.prefetchQueue.length = 0;
    this.prefetchInFlight = 0;

    this.api.getCreatives().subscribe({
      next: (r) => {
        const list = r.items ?? [];
        this.items.set(list);
        this.loading.set(false);
        this.schedulePublishOptionsPrefetch(list);
      },
      error: (err) => this.failLoad(err)
    });
  }

  getDestinations(c: AiCreativeListItem): AiPublishDestination[] {
    return this.publishOptionsByCreativeId()[c.id] ?? [];
  }

  isLoadingPublishOptions(c: AiCreativeListItem): boolean {
    return this.loadingPublishOptionsIds()[c.id] === true;
  }

  private schedulePublishOptionsPrefetch(items: AiCreativeListItem[]): void {
    for (const creative of items) {
      if (creative.status !== 'Approved') {
        continue;
      }
      if (this.publishOptionsByCreativeId()[creative.id] || this.loadingPublishOptionsIds()[creative.id]) {
        continue;
      }
      this.prefetchQueue.push(creative.id);
    }
    this.drainPrefetchQueue();
  }

  private drainPrefetchQueue(): void {
    while (this.prefetchInFlight < this.maxPrefetchConcurrent && this.prefetchQueue.length > 0) {
      const creativeId = this.prefetchQueue.shift();
      if (!creativeId) {
        return;
      }
      this.fetchPublishOptions(creativeId, { silent: true, showError: false, fromQueue: true });
    }
  }

  private fetchPublishOptions(
    creativeId: string,
    options: { silent?: boolean; showError?: boolean; fromQueue?: boolean } = {}
  ): void {
    const silent = options.silent ?? false;
    const showError = options.showError ?? true;
    const fromQueue = options.fromQueue ?? false;

    if (this.publishOptionsByCreativeId()[creativeId] || this.loadingPublishOptionsIds()[creativeId]) {
      return;
    }

    this.loadingPublishOptionsIds.update((m) => ({ ...m, [creativeId]: true }));
    if (fromQueue) {
      this.prefetchInFlight++;
    }
    this.api.getPublishDestinations(creativeId, silent).subscribe({
      next: (r) => {
        this.publishOptionsByCreativeId.update((m) => ({ ...m, [creativeId]: r.items ?? [] }));
        this.loadingPublishOptionsIds.update((m) => ({ ...m, [creativeId]: false }));
        if (fromQueue) {
          this.prefetchInFlight = Math.max(0, this.prefetchInFlight - 1);
          this.drainPrefetchQueue();
        }
      },
      error: (err) => {
        this.publishOptionsByCreativeId.update((m) => ({ ...m, [creativeId]: [] }));
        this.loadingPublishOptionsIds.update((m) => ({ ...m, [creativeId]: false }));
        if (fromQueue) {
          this.prefetchInFlight = Math.max(0, this.prefetchInFlight - 1);
          this.drainPrefetchQueue();
        }
        if (showError) {
          this.fail(err);
        }
      }
    });
  }

  statusKey(status: string): TranslationKey {
    return `aiMarketing.creatives.status.${status}` as TranslationKey;
  }

  toggleCreateForm(): void {
    this.showCreateForm.update((open) => !open);
  }

  closeCreateForm(): void {
    this.showCreateForm.set(false);
  }

  sourceKey(source: string): TranslationKey {
    return `aiMarketing.creatives.source.${source}` as TranslationKey;
  }

  typeKey(type: string): TranslationKey {
    return `aiMarketing.creatives.type.${type}` as TranslationKey;
  }

  platformKey(platform: string): TranslationKey {
    return `aiMarketing.platform.${platform}` as TranslationKey;
  }

  canReview(c: AiCreativeListItem): boolean {
    return c.status === 'Generated';
  }

  canDelete(c: AiCreativeListItem): boolean {
    return (c.publishedPlatforms?.length ?? 0) === 0;
  }

  canPublish(c: AiCreativeListItem): boolean {
    return c.status === 'Approved';
  }

  startPublish(c: AiCreativeListItem, platform: string): void {
    this.publishTarget.set(c);
    this.publishPlatform.set(platform);
    this.publishCaption.set((c.publicationTitle || c.prompt || '').trim());
    if (!this.publishOptionsByCreativeId()[c.id]) {
      this.fetchPublishOptions(c.id, { showError: true });
    }
  }

  cancelPublish(): void {
    this.publishTarget.set(null);
    this.publishPlatform.set(null);
    this.publishCaption.set('');
  }

  confirmPublish(): void {
    const c = this.publishTarget();
    const platform = this.publishPlatform();
    const destination = this.activePublishDestination();
    if (!c || !platform || !destination || !this.canConfirmPublish()) {
      return;
    }

    const label = `${this.i18n.t(this.platformKey(platform))} (${destination.label})`;

    void this.confirmDialog
      .confirm({
        title: this.i18n.t('aiMarketing.creatives.publishConfirmTitle'),
        message: this.i18n.t('aiMarketing.creatives.publishConfirmMessage', { destinations: label })
      })
      .then((ok) => {
        if (!ok) {
          return;
        }
        this.publishing.set(true);
        this.httpLoading.begin();
        this.api
          .publishOrganicFromCreative({
            creativeId: c.id,
            caption: this.publishCaption(),
            platforms: [platform]
          })
          .subscribe({
            next: (result) => {
              this.publishing.set(false);
              this.httpLoading.end();
              if (result.errors?.length) {
                const partial = result.errors.map((e) => `${e.platform}: ${e.error}`).join('\n');
                void this.confirmDialog.alert({
                  title: this.i18n.t('aiMarketing.creatives.publishPartialTitle'),
                  message: partial
                });
              }
              this.cancelPublish();
              this.reload();
            },
            error: (err) => {
              this.publishing.set(false);
              this.httpLoading.end();
              this.fail(err);
            }
          });
      });
  }

  onFilePicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile.set(input.files?.[0] ?? null);
  }

  upload(): void {
    const file = this.selectedFile();
    if (!file) {
      return;
    }
    const form = new FormData();
    form.append('file', file);
    this.uploading.set(true);
    this.api.uploadCreative(form).subscribe({
      next: () => {
        this.uploading.set(false);
        this.selectedFile.set(null);
        this.closeCreateForm();
        this.reload();
      },
      error: (err) => {
        this.uploading.set(false);
        this.fail(err);
      }
    });
  }

  onProductSearchInput(value: string): void {
    this.productSearch.set(value);
    if (this.productSearchTimer) {
      clearTimeout(this.productSearchTimer);
    }
    this.productSearchTimer = setTimeout(() => this.searchProducts(), 300);
  }

  searchProducts(): void {
    const q = this.productSearch().trim();
    if (!q) {
      this.productResults.set([]);
      return;
    }
    this.loadingProducts.set(true);
    this.productsApi.getPaged({ search: q, page: 1, pageSize: 12 }).subscribe({
      next: (r) => {
        this.productResults.set(r.items ?? []);
        this.loadingProducts.set(false);
      },
      error: (err) => {
        this.loadingProducts.set(false);
        this.fail(err);
      }
    });
  }

  selectProductFromList(item: ProductListItem): void {
    this.productResults.set([]);
    this.loadingProducts.set(true);
    this.productsApi.getById(item.id).subscribe({
      next: (product) => {
        this.loadingProducts.set(false);
        this.selectedProduct.set(product);
        this.productPrompt.set(product.name);
        const primary = product.images?.find((i) => i.isPrimary) ?? product.images?.[0];
        this.selectedImageId.set(primary?.id ?? null);
      },
      error: (err) => {
        this.loadingProducts.set(false);
        this.fail(err);
      }
    });
  }

  clearSelectedProduct(): void {
    this.selectedProduct.set(null);
    this.selectedImageId.set(null);
    this.productPrompt.set('');
  }

  selectProductImage(imageId: string): void {
    this.selectedImageId.set(imageId);
  }

  createFromProduct(): void {
    const product = this.selectedProduct();
    const imageId = this.selectedImageId();
    if (!product || !imageId) {
      return;
    }
    this.creatingFromProduct.set(true);
    this.api
      .createCreativeFromProduct({
        productId: product.id,
        imageId,
        prompt: this.productPrompt()
      })
      .subscribe({
        next: () => {
          this.creatingFromProduct.set(false);
          this.clearSelectedProduct();
          this.productSearch.set('');
          this.closeCreateForm();
          this.reload();
        },
        error: (err) => {
          this.creatingFromProduct.set(false);
          this.fail(err);
        }
      });
  }

  approve(id: string): void {
    this.actionId.set(id);
    this.api.approveCreative(id).subscribe({
      next: () => {
        this.actionId.set(null);
        this.reload();
      },
      error: (err) => {
        this.actionId.set(null);
        this.fail(err);
      }
    });
  }

  deleteCreative(id: string): void {
    void this.confirmDialog
      .confirm({
        title: this.i18n.t('aiMarketing.creatives.deleteTitle'),
        message: this.i18n.t('aiMarketing.creatives.deleteMessage')
      })
      .then((ok) => {
        if (!ok) return;
        this.actionId.set(id);
        this.api.deleteCreative(id).subscribe({
          next: () => {
            this.actionId.set(null);
            if (this.publishTarget()?.id === id) {
              this.cancelPublish();
            }
            this.reload();
          },
          error: (err) => {
            this.actionId.set(null);
            this.fail(err);
          }
        });
      });
  }

  private failLoad(err: unknown): void {
    this.loading.set(false);
    this.loadError.set(this.extractErrorMessage(err));
  }

  private fail(err: unknown): void {
    void this.confirmDialog.alert({
      title: this.i18n.t('common.errorTitle'),
      message: this.extractErrorMessage(err)
    });
  }

  private extractErrorMessage(err: unknown): string {
    if (err && typeof err === 'object') {
      const httpErr = err as { error?: unknown; message?: string; status?: number };
      const body = httpErr.error;
      if (typeof body === 'string' && body.trim()) {
        if (body.includes('<!DOCTYPE') || body.includes('Gateway Timeout')) {
          return 'The API timed out (CloudFront 504). Wait a few seconds and try again.';
        }
        return body;
      }
      if (body && typeof body === 'object' && 'error' in body) {
        const nested = (body as { error?: unknown }).error;
        if (typeof nested === 'string' && nested.trim()) {
          return nested;
        }
      }
      if (httpErr.status === 404) {
        return 'API endpoint not found. Deploy the latest API to Aws and try again.';
      }
      if (httpErr.status === 502 || httpErr.status === 504) {
        return 'The API timed out. Wait a few seconds and try again.';
      }
      if (httpErr.message?.trim()) {
        return httpErr.message;
      }
    }
    return this.i18n.t('aiMarketing.loadError');
  }
}
